// lib/leasing/sourcingCampaignLifecycle.ts
//
// The rules that decide when a sourcing campaign is allowed to stop being "in progress", and what
// it must be left as. They live here, pure and free of Prisma, because THREE places have to agree
// on them and previously only one of them implemented any of it:
//
//   - worker/processors/sourcingCampaignExecution.ts  (the pass itself, every exit path)
//   - worker/index.ts                                 (a queue message that died of old age)
//   - app/api/inventory/campaigns/route.ts            (the dashboard list read, which is the ONLY
//                                                      reconciler that still runs when no worker is alive)
//
// Why this exists at all. A campaign row is the progress surface the shortlist reads: PENDING /
// WAITING / RUNNING mean "work is happening", anything else means it is over. A row left on one of
// the first three is a run that shows as working forever AND blocks its asset's next launch,
// because the launch guardrail refuses a second active campaign. That is exactly what happened to
// Espace Saint Quentin: a single-brand re-run parked the campaign in WAITING, enqueued its job, and
// the job was never consumed. Nothing anywhere ever looked at that row again.
//
// The invariant these helpers exist to enforce: a campaign can never sit in a non-terminal status
// past its deadline. Every exit path settles, the queue message carries a deadline, and the read
// path settles whatever the first two missed.

/** Statuses that mean "work in progress". Everything else is a resting state. */
export const ACTIVE_SOURCING_CAMPAIGN_STATUSES = ['PENDING', 'WAITING', 'RUNNING'] as const;

/**
 * Kept as the complement of the active list rather than a list of terminal names, so an unusual
 * status on an old row is preserved instead of being rewritten into one we happen to know about.
 */
export const isTerminalSourcingCampaignStatus = (status: unknown): status is string =>
  typeof status === 'string' &&
  status.length > 0 &&
  !(ACTIVE_SOURCING_CAMPAIGN_STATUSES as readonly string[]).includes(status);

/**
 * How long a queued sourcing job stays worth running. Past this the worker discards the message
 * rather than starting a billed pass the user stopped waiting for hours ago, and the read path is
 * allowed to declare the campaign dead. Generous on purpose: it only has to outlast a queue
 * backlog, never a pass.
 */
export const SOURCING_JOB_TTL_MS = 6 * 60 * 60 * 1000;

/**
 * The fallback deadline for a campaign that carries no `config.expiresAt`: rows launched before
 * the deadline was recorded, and rows whose worker cleared it on pickup and then died mid-pass.
 * Measured against `updatedAt`, which a live pass keeps bumping through its progress writes, so
 * this only ever fires on a run that has genuinely gone silent.
 */
export const SOURCING_CAMPAIGN_SILENCE_CEILING_MS = 6 * 60 * 60 * 1000;

/**
 * How long a row must have been quiet before the DEADLINE branch is allowed to declare it dead.
 *
 * The deadline says "the worker would discard this message", which is only true if no worker ever
 * took it, and a worker that takes it clears `config.expiresAt` on pickup. But that clear is a
 * best-effort write wrapped in a try/catch, so a single failed clear left a LIVE, billed pass
 * carrying a deadline it would cross at launch+6h, and the next list read would close it under its
 * own feet while it was still writing scores. A live pass writes progress every second or so, so a
 * quarter of an hour of silence is conclusive without being anywhere near a real pass's cadence.
 */
export const SOURCING_CAMPAIGN_LIVE_PASS_SILENCE_MS = 15 * 60 * 1000;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Trim, drop the blanks, dedupe, keep the caller's order. The order is the one the user selected
 * the rows in, and it is what the pass analyses in, so it is preserved rather than sorted.
 */
export const normalizeBrandIdList = (value: unknown): string[] => {
  const raw = Array.isArray(value) ? value : [value];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const item of raw) {
    if (typeof item !== 'string') continue;
    const id = item.trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
};

/**
 * EVERY ASSET ONE CAMPAIGN COVERS, in the order the run analyses them.
 *
 * A sourcing run used to be one asset because `SourcingCampaign.propertyId` is one column. Picking
 * three assets therefore produced three campaigns wearing one button. The coverage now lives in
 * `config.propertyIds` instead, and the column keeps holding the FIRST of them, which is what every
 * single-asset caller (tenancy checks, the queue message, the re-run route, notifications) still
 * reads and what the `Lead.lastCampaignId` foreign key needs to stay satisfiable.
 *
 * So the column is not "the asset" any more, it is "the primary asset", and this function is the one
 * place that says what the whole run reaches. A campaign written before this existed has no
 * `config.propertyIds` and resolves to exactly its own column, i.e. unchanged.
 *
 * `columnPropertyIds` is a scalar on `SourcingCampaign` and an array on the unified `Campaign`
 * projection, so both are accepted: the same question, asked of the same run through two shapes.
 * The column comes first, because it is the one the rest of the system trusts, and a portfolio-wide
 * run legitimately has none.
 *
 * This is also what the dashboard card reads through `campaignCoverageLabel`, which knows nothing
 * but `propertyIds.length`. Reading only the column would print "one asset" for a three-asset run;
 * reading only the config would print "whole portfolio" for every campaign that predates this.
 */
export const resolveCampaignPropertyIds = (
  config: unknown,
  columnPropertyIds: string | readonly string[] | null | undefined,
): string[] => {
  const fromColumn = normalizeBrandIdList(
    Array.isArray(columnPropertyIds) ? columnPropertyIds : [columnPropertyIds],
  );
  // An ARRAY or nothing. `normalizeBrandIdList` accepts a bare string as a one-element list, which
  // is right for a re-run marker and wrong here: a config that somehow holds a string would become
  // a second asset and send the pass looking up a property that does not exist.
  const declared =
    isRecord(config) && Array.isArray(config.propertyIds) ? normalizeBrandIdList(config.propertyIds) : [];
  return normalizeBrandIdList([...fromColumn, ...declared]);
};

/**
 * How a launch budget is shared between the assets it covers. One credit buys one brand analysis,
 * and a brand analysed against three assets is three analyses, so the budget is split, never
 * multiplied: picking three assets on a 60-credit budget must cost 60 credits, not 180.
 *
 * `availableByProperty` is how many eligible brands each asset actually has. An even split alone
 * wastes the budget whenever the assets are uneven (60 credits over an asset with 200 candidates
 * and one with 3 would strand 27 credits), so what an asset cannot absorb is handed to the ones
 * that can. Deterministic, order-preserving, and never allocates more than either the budget or the
 * candidates allow, which is what makes it safe to bill against.
 */
export const allocateCampaignBudget = (budget: number, availableByProperty: readonly number[]): number[] => {
  const available = availableByProperty.map((n) =>
    Number.isFinite(n) && n > 0 ? Math.floor(n) : 0,
  );
  const total = Math.max(0, Math.floor(Number.isFinite(budget) ? budget : 0));
  if (available.length === 0 || total === 0) return available.map(() => 0);

  // Even share first, capped by what each asset has.
  const base = Math.floor(total / available.length);
  const allocated = available.map((cap) => Math.min(cap, base));
  // Then hand out what is left, one credit at a time, to whoever still has room. One pass per
  // credit is bounded by the budget slider (SOURCING_BUDGET_MAX), so this cannot run away.
  let remaining = total - allocated.reduce((sum, n) => sum + n, 0);
  while (remaining > 0) {
    const before = remaining;
    for (let i = 0; i < allocated.length && remaining > 0; i += 1) {
      if (allocated[i]! >= available[i]!) continue;
      allocated[i] = allocated[i]! + 1;
      remaining -= 1;
    }
    // Nobody could absorb anything this pass: every asset is at its ceiling.
    if (remaining === before) break;
  }
  return allocated;
};

export type BrandRerunMarker = {
  /** Every brand this pass was asked to re-analyse, in the order they were requested. */
  brandIds: string[];
  previousStatus: string;
};

/**
 * The re-run marker the rerun-brand route writes into `config`, if there is a live one.
 *
 * TWO shapes are read, and that is deliberate. The marker used to name ONE brand (`brandId`) and a
 * marker written by the previous shape can still be sitting on a campaign row right now, on a pass
 * that is mid-flight. Reading only the new `brandIds` would make that pass look like a full launch,
 * whose target set is "every lead still PENDING", which is precisely the over-billing this whole
 * mechanism exists to prevent. So the new list is preferred and the single form is the fallback.
 *
 * `previousStatus` is the terminal status the campaign held before it lent its lifecycle to the
 * pass; older markers do not carry it, hence the optional read.
 */
export const readBrandRerunMarker = (config: unknown): BrandRerunMarker | null => {
  if (!isRecord(config)) return null;
  const marker = config.brandRerun;
  if (!isRecord(marker)) return null;
  const brandIds = normalizeBrandIdList(marker.brandIds);
  const resolved = brandIds.length > 0 ? brandIds : normalizeBrandIdList(marker.brandId);
  if (resolved.length === 0) return null;
  return {
    brandIds: resolved,
    previousStatus: isTerminalSourcingCampaignStatus(marker.previousStatus)
      ? marker.previousStatus
      : '',
  };
};

/**
 * Is THIS job a re-run, and of which brands?
 *
 * The job is asked first and the config marker is only a fallback. A marker in `config` describes
 * whatever pass wrote it and nothing clears it on the way out of a route, so reading it on a job
 * that is not a re-run is how a full launch would silently shrink to one brand. The job is the only
 * thing that knows what it was enqueued to do.
 *
 * `jobRerunBrandId` is the single-brand field the job carried before the selection re-run existed.
 * A message enqueued by the old shape can still be in the queue, so it stays readable here.
 */
export const resolveRerunBrandIds = (params: {
  jobRerunBrandIds?: readonly string[] | undefined;
  jobRerunBrandId?: string | undefined;
  config: unknown;
}): string[] => {
  const fromJob = normalizeBrandIdList(params.jobRerunBrandIds);
  if (fromJob.length > 0) return fromJob;
  const fromLegacyJob = normalizeBrandIdList(params.jobRerunBrandId);
  if (fromLegacyJob.length > 0) return fromLegacyJob;
  return readBrandRerunMarker(params.config)?.brandIds ?? [];
};

/**
 * The status to hand a campaign back to when a pass ends without completing.
 *
 * A normal launch owns its row, so it settles FAILED. A single-brand re-run BORROWS a finished
 * campaign's row, so it has to return the status that row already had: a re-run must never be able
 * to turn a COMPLETE shortlist into a failure, nor an ABORTED one into a success. When every copy
 * of the previous status is gone the answer is FAILED and never COMPLETE, because inventing a
 * success is the one wrong answer; leaving the row non-terminal is not an option, since that blocks
 * the asset's next launch forever.
 */
export const resolveSettleStatus = (params: {
  isRerun: boolean;
  config: unknown;
  jobPreviousStatus?: string | undefined;
}): string => {
  if (!params.isRerun) return 'FAILED';
  const marker = readBrandRerunMarker(params.config);
  if (marker?.previousStatus) return marker.previousStatus;
  if (isTerminalSourcingCampaignStatus(params.jobPreviousStatus)) return params.jobPreviousStatus;
  return 'FAILED';
};

/**
 * The brands this pass will actually analyse: the declared scope intersected with the leads that
 * are still PENDING.
 *
 * A re-run NAMES its brands and that list REPLACES the declared scope rather than filtering it: a
 * launch that was stopped, budget-capped or interrupted leaves leads PENDING for brands it never
 * reached, and "everything still pending" would drag those into a pass the user asked for on a
 * handful of rows, and bill for them. So the guarantee this function owes its caller is exact:
 * when a re-run scope is given, the result is a SUBSET of that scope and of nothing else. The
 * campaign's declared targets are not even consulted, because a re-run's requested brands were
 * already checked against them by the route that queued it.
 */
export const resolveSourcingTargetBrandIds = (params: {
  campaignTargetBrandIds: readonly string[];
  pendingBrandIds: readonly string[];
  rerunBrandIds?: readonly string[];
}): string[] => {
  const pending = new Set(params.pendingBrandIds.filter((id) => typeof id === 'string' && id.length > 0));
  const rerunBrandIds = normalizeBrandIdList(params.rerunBrandIds);
  if (rerunBrandIds.length > 0) return rerunBrandIds.filter((id) => pending.has(id));
  const declared = normalizeBrandIdList(params.campaignTargetBrandIds);
  if (declared.length === 0) return Array.from(pending);
  return declared.filter((id) => pending.has(id));
};

/**
 * What to tell the user when the pass resolves to nothing to do. It is never "done": the run the
 * user asked for did not happen, and a silent COMPLETE on an empty set is indistinguishable from a
 * shortlist that scored perfectly, which is how a broken re-run reads as a successful one.
 */
export const describeEmptyTargetSet = (params: {
  isRerun: boolean;
  hasDeclaredTargets: boolean;
  /** How many brands the re-run asked for, so the sentence matches what the user clicked. */
  rerunCount?: number;
}): string => {
  if (params.isRerun) {
    return (params.rerunCount ?? 1) > 1
      ? 'None of the selected brands were still queued for analysis when the run started, so nothing was re-analysed. Select them again from the shortlist to try again.'
      : 'This brand was no longer queued for analysis when the run started, so nothing was re-analysed. Re-run it from the shortlist to try again.';
  }
  if (!params.hasDeclaredTargets) {
    return 'This run had no brands to analyse: nothing on the shortlist was waiting for a score. Launch a new sourcing to score brands.';
  }
  return 'None of this run\'s target brands were still waiting for a score when it started, so nothing was analysed. Launch a new sourcing to score them.';
};

/**
 * Merge a note into `metrics.worker` without dropping the rest of the metrics. The atomic-JSON rule
 * holds: the whole object is written and emitted, never a delta. Merging rather than replacing
 * matters on a re-run, where `metrics` still carries the ORIGINAL run's progress, the figures the
 * shortlist header shows.
 */
export const buildCampaignWorkerMetrics = (
  previousMetrics: unknown,
  update: Record<string, unknown>,
): Record<string, unknown> => {
  const base = isRecord(previousMetrics) ? previousMetrics : {};
  const previousWorker = isRecord(base.worker) ? base.worker : {};
  return { ...base, worker: { ...previousWorker, ...update } };
};

/**
 * Group brands by the `portfolioFitId` a re-queue is about to write on them.
 *
 * The launch path re-queues every lead that already exists, and the row it writes is identical for
 * all of them EXCEPT that one column. Writing brand by brand costs one round-trip per brand, inside
 * an interactive transaction that holds a single connection and write locks on `Lead` in production
 * (doctrine 06, §4.A + §4.D). Grouped, the number of statements comes from the number of distinct
 * fit values — one or two in practice — instead of from the campaign budget.
 *
 * Order is preserved (groups in first-seen order, brands in input order) and the union of the
 * groups is exactly the input: same brands, no duplicate, none lost. That is what makes the grouped
 * write equivalent to the per-brand loop it replaces.
 */
export const groupBrandIdsByPortfolioFitId = (
  brandIds: readonly string[],
  fitByBrandId: ReadonlyMap<string, string | null>,
): { portfolioFitId: string | null; brandIds: string[] }[] => {
  const groups = new Map<string | null, string[]>();
  for (const brandId of brandIds) {
    const portfolioFitId = fitByBrandId.get(brandId) ?? null;
    const existing = groups.get(portfolioFitId);
    if (existing) existing.push(brandId);
    else groups.set(portfolioFitId, [brandId]);
  }
  return [...groups].map(([portfolioFitId, ids]) => ({ portfolioFitId, brandIds: ids }));
};

/**
 * The config a settled campaign should carry: no live re-run marker, no queue deadline.
 *
 * Clearing the marker is what stops a stale one scoping a later run, and clearing `expiresAt` is
 * what stops the read-path reconciler settling the same row twice.
 */
export const configAfterSettle = (config: unknown): Record<string, unknown> => {
  const base = isRecord(config) ? { ...config } : {};
  delete base.brandRerun;
  delete base.expiresAt;
  return base;
};

/** The deadline written into `config` at launch/re-run, if it is still there and readable. */
export const readCampaignDeadline = (config: unknown): number | null => {
  if (!isRecord(config)) return null;
  if (typeof config.expiresAt !== 'string') return null;
  const parsed = Date.parse(config.expiresAt);
  return Number.isFinite(parsed) ? parsed : null;
};

export type CampaignStallVerdict = {
  settleStatus: string;
  lastError: string;
};

/**
 * Should this campaign be declared dead, and as what?
 *
 * This is the last line of defence, and the only one that still works when no worker is alive at
 * all: a queue deadline only fires when something DEQUEUES the message, so a queue nobody polls
 * leaves the campaign in WAITING forever no matter how carefully the worker settles its own exits.
 *
 * Two independent signals, both deliberately generous, because the cost of firing early is killing
 * a live run:
 *   - the queue deadline has passed, so the worker would discard that message on sight;
 *   - the row has been silent past the ceiling, which a live pass cannot be: it writes progress
 *     into `metrics` as it goes and every such write bumps `updatedAt`.
 */
export const resolveCampaignStall = (params: {
  status: string;
  config: unknown;
  updatedAt: Date | string;
  jobPreviousStatus?: string;
  now?: number;
}): CampaignStallVerdict | null => {
  if (isTerminalSourcingCampaignStatus(params.status)) return null;
  const now = params.now ?? Date.now();
  const updatedAtMs =
    params.updatedAt instanceof Date ? params.updatedAt.getTime() : Date.parse(String(params.updatedAt));

  const silentForMs = Number.isFinite(updatedAtMs) ? now - updatedAtMs : Number.POSITIVE_INFINITY;
  const deadline = readCampaignDeadline(params.config);
  // A queued row (PENDING/WAITING) is by definition not being worked, so an expired deadline closes
  // it on sight. A RUNNING row is the dangerous case: a worker took it and should have cleared
  // `expiresAt`, but that clear is best-effort in a try/catch, so one failed clear used to be
  // enough for this read to close a live, billed pass at launch+6h while it was still writing
  // scores. There, the deadline needs the same liveness proof the silence branch always had.
  const isWorkedStatus = params.status === 'RUNNING';
  const expiredByDeadline =
    deadline !== null &&
    now > deadline &&
    (!isWorkedStatus || silentForMs > SOURCING_CAMPAIGN_LIVE_PASS_SILENCE_MS);
  const expiredBySilence = silentForMs > SOURCING_CAMPAIGN_SILENCE_CEILING_MS;
  if (!expiredByDeadline && !expiredBySilence) return null;

  const marker = readBrandRerunMarker(params.config);
  const settleStatus = resolveSettleStatus({
    isRerun: Boolean(marker),
    config: params.config,
    jobPreviousStatus: params.jobPreviousStatus,
  });
  return {
    settleStatus,
    lastError: expiredByDeadline
      ? 'This run never reached a worker before its deadline, so it was closed. Launch it again.'
      : 'This run stopped reporting progress and was closed after a long silence. Launch it again.',
  };
};
